import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { Panel, PanelText, Trigger, Wrap } from './EstimateInfo.styles';

export type EstimateInfoProps = {
  /** Line the panel up with the box's start (default) or end edge (a trigger near the right edge). */
  align?: 'start' | 'end';
  /** The plan carries a fixed fee: say it is left out of the per-player figures. */
  fixedFee?: boolean;
};

/**
 * The info button beside an estimated figure (the affiliate's commission and what the brand keeps after it): why
 * those figures are estimates. Opens on hover, on focus and on click; closes on mouse leave, on blur and on Escape;
 * linked with `aria-describedby`, so the copy is announced rather than pointer-only; a press outside also closes it. A click OPENS rather than toggles:
 * a tap on a phone fires the hover, the focus and the click in one gesture, and a toggle would close what the first two
 * just opened. The caller supplies the panel's containing block (`position: relative`), which the panel opens under,
 * never wider than it.
 */
export const EstimateInfo = ({ align = 'start', fixedFee = false }: EstimateInfoProps): React.ReactElement => {
  const { t } = useTranslation();
  const id = React.useId();
  const [open, setOpen] = React.useState(false);

  const wrapRef = React.useRef<HTMLSpanElement>(null);

  // While open: Escape closes the panel only, and a press anywhere else closes it. Escape is caught in the capture
  // phase and stopped, so the sheet around the button (which closes on Escape too) never hears it. The outside press
  // is what closes it on iOS, where a tap doesn't focus the button and so no blur follows.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
    };
    const onPointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && wrapRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open]);

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type="button"
        aria-label={t('Why estimated?')}
        aria-describedby={id}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(true)}
      >
        <Icon name="info" size={14} color="currentColor" />
      </Trigger>
      <Panel id={id} role="tooltip" hidden={!open} $align={align}>
        <PanelText
          text="Estimated from this player's activity and the affiliate's commission plan. The affiliates platform settles commission monthly across all of an affiliate's players, so the amount paid can differ."
          tag="span"
        />
        {fixedFee ? (
          <PanelText
            text="The plan's fixed fee is charged per affiliate each month, not per player, so it isn't included."
            tag="span"
          />
        ) : null}
      </Panel>
    </Wrap>
  );
};

export default EstimateInfo;
